import { z } from "zod";

import { SubjectType } from "../permissions/permissions.types";

/**
 * Who is in a permission set — the wire shape of one `RoleGroupMember` row.
 *
 * 🚨 NOT `RoleMember`, the members-list shape of one ADMIN or MEMBER. This is one
 * seat in one permission set INSIDE a Role, and the seat is what membership is
 * read from: a `user` seat in any set places the person in the Role, and a seat
 * in the `maintainer` or `member` set is their ADMIN or MEMBER tier — the seat a
 * `RoleMember` is read off. A permission set is a subset of the Role's
 * ORGANIZATION. The two names are one word apart and the shapes are different.
 *
 * ⚠️ IT LIVES HERE RATHER THAN BESIDE ITS RESPONSE SCHEMA, AND THAT IS
 * LOAD-BEARING — but the reason is REACH, not a runtime hazard.
 * `packages/types/src/api/domains/**` has no wholesale re-export at the package
 * root: `api/index.ts` pulls the domains in through 200 `import type` statements
 * to assemble `TApi`, and re-exports individual symbols only by NAME, through
 * explicit `export { … }` and `export type { … }` blocks. So a symbol nobody has
 * added to one of those blocks is not reachable from `@nexus/types` at all, and
 * declaring the type here is how it becomes reachable without editing that barrel.
 *
 * 🚨 IT IS NOT A LATENT RUNTIME CRASH, AND SAYING SO SENDS THE NEXT READER HUNTING
 * A CLASS THAT DOES NOT EXIST. `tsc` REFUSES the import — measured against the
 * backend, the frontend and `packages/shared`, each with a control error proving
 * the compiler read the probe: an absent symbol is `TS2305 Module '"@nexus/types"'
 * has no exported member`, and a value reached through `export type` is `TS1362`
 * at the use site. Both are hard errors, so `pnpm typecheck` cannot go green over
 * one. `PermissionSetDeleteResponseSchema` does resolve to `undefined` off the built
 * package — but nothing can import it to find out.
 */

/**
 * The three subject kinds a permission set may hold.
 *
 * `SubjectType` has five members; `api_key` and `role` are legal on
 * `ResourcePermission` and meaningless here — an API key is not a person in a
 * permission set, and a Role inside its own Role's set is a cycle. The database
 * refuses both through the `RoleGroupMember_subject_known` CHECK, so this is the
 * contract half of a constraint that already exists.
 */
export const PERMISSION_SET_MEMBER_SUBJECT_TYPES = [
  SubjectType.USER,
  SubjectType.GROUP,
  SubjectType.ORGANIZATION
] as const;

export const PermissionSetMemberSubjectTypeSchema = z.enum(PERMISSION_SET_MEMBER_SUBJECT_TYPES);

export type PermissionSetMemberSubjectType = z.infer<typeof PermissionSetMemberSubjectTypeSchema>;

export const PermissionSetMemberSchema = z.object({
  id: z.string(),
  roleGroupId: z.string(),
  roleId: z.string(),
  subjectType: PermissionSetMemberSubjectTypeSchema,
  /**
   * THREE ID SHAPES IN ONE COLUMN, which is why it is a bare string and why the
   * column carries no foreign key: a Clerk `User.id` or `Organization.id` for
   * `user` / `organization`, and a `UserGroup.id` UUID for `group`.
   */
  subjectId: z.string(),
  /** Informational only, no FK. `null` when the write had no acting user. */
  addedByUserId: z.string().nullable(),
  createdAt: z.string()
});

export type RoleGroupMember = z.infer<typeof PermissionSetMemberSchema>;
